import React, { useEffect, useMemo, useCallback, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomOptions from "../../Components/CustomOptions";

const ViewReport = ({ pickupDuration, reportPayload, setReportPayload, selectedPropCount = 0, canUpdate = true, onPermissionDenied = null }) => {

  const columns = useMemo(() => ({
    Details: ["Date", "Day Of Week", "Total Rooms", "Transient Rooms", "Group Rooms"],
    OTBActuals: ["Current Rooms Sold","Current Occupancy %", "Current ADR", "Current RevPAR", "Current Room Revenue"],
    Pickup: ["Rooms", "Occupancy %", "ADR", "Revenue"],
    Budget: ["Budget Rooms Sold", "Budget Occupancy %", "Budget ADR", "Budget RevPAR", "Budget Room Revenue"],
    DayVariance: ["Rooms Sold Var (Budget)", "Occupancy Var (Budget)", "ADR Variance (Budget)", "Room Revenue Var (Budget)"],
    LYActuals: ["LY Rooms Sold", "LY Occupancy %", "LY ADR", "LY RevPAR", "LY Room Revenue"],
    LYDayVariance: ["Rooms Sold Variance (LY)", "Occupancy Variance (LY)", "ADR Variance (LY)", "Room Revenue Var (LY)"],
  }), []);

  const columnMapping = useMemo(() => ({
    "Date": "Date",
    "Day Of Week": "DOW",
    "Total Rooms": "TotalRooms",
    "Transient Rooms": "TrnsRooms",
    "Group Rooms": "GrpRooms",
    "Current Rooms Sold": "OTBRoomsSold",
    "Current Occupancy %": "OTBOcc",
    "Current ADR": "OTBADR",
    "Current Room Revenue": "OTBRoomRev",
    "Current RevPAR": "OTBRevPar",
    "Rooms": "PickUpRooms",
    "ADR": "PickUpADR",
    "Revenue": "PickUpRev",
    "Occupancy %": "PickUpOcc",
    "Budget Rooms Sold": "BudgetRoomsSold",
    "Budget Occupancy %": "BudgetOcc",
    "Budget ADR": "BudgetAdr",
    "Budget Room Revenue": "BudgetRoomRev",
    "Budget RevPAR": "BudgetRevPar",
    "Rooms Sold Var (Budget)": "RoomsSoldVariance",
    "ADR Variance (Budget)": "ADRVariance",
    "Room Revenue Var (Budget)": "RoomRevenueVariance",
    "Occupancy Var (Budget)": "OccVariance",
    "LY Rooms Sold": "LYRoomsSold",
    "LY Occupancy %": "LYOccupancy",
    "LY ADR": "LYADR",
    "LY Room Revenue": "LYRoomRev",
    "LY RevPAR": "LYRevPar",
    "Rooms Sold Variance (LY)": "LYRoomsSoldVariance",
    "ADR Variance (LY)": "LYADRVariance",
    "Room Revenue Var (LY)": "LYRoomRevenueVariance",
    "Occupancy Variance (LY)": "LYOccVariance",
  }), []);

  const columnTitles = useMemo(() => ({
    Details: "Details",
    OTBActuals: "OTB Actuals",
    Pickup: "Pickup",
    Budget: "Budget",
    DayVariance: "Day Variance",
    LYActuals: "LY Actuals",
    LYDayVariance: "Day Variance",
  }), []);

  const mandatoryFields = useMemo(() => [
    "Date", 
    "DOW", 
    "Current Rooms Sold", 
    "Rooms", // Pickup
    "ADR",  // Pickup
    "Budget Rooms Sold", 
    "Budget Occupancy %", 
    "Rooms Sold Var (Budget)", 
    "ADR Variance (Budget)", 
    "LY Rooms Sold", 
    "LY Occupancy %", 
    "Rooms Sold Variance (LY)", 
    "ADR Variance (LY)", 
    "Occupancy Variance (LY)"
  ], []);

  useEffect(() => {
    setReportPayload((prev) => {
      const newPayload = { ...prev };
      if (pickupDuration === "N/A") {
        newPayload.PickUpRooms = false;
        newPayload.PickUpADR = false;
        newPayload.PickUpRev = false;
        newPayload.PickUpOcc = false;
      }
      return newPayload;
    });
  }, [pickupDuration, setReportPayload]);

  const prevPropCountRef = useRef(selectedPropCount);

  // Business Rules 1 & 4
  useEffect(() => {
    setReportPayload((prev) => {
      const newPayload = { ...prev };
      // Rule 1: Date and Curr Rm Sold read-only (always true)
      newPayload.OTBRoomsSold = true;
      
      // Rule 4: If > 1 property, disable Date/DOW (unchecked).
      if (selectedPropCount > 1) {
        newPayload.Date = false;
        newPayload.DOW = false;
      } else if (prevPropCountRef.current > 1 && selectedPropCount <= 1) {
        // When coming from multiple corporation to single corporation,
        // Date and DOW need to go true by default until user deselects manually
        newPayload.Date = true;
        newPayload.DOW = true;
      }
      return newPayload;
    });
    prevPropCountRef.current = selectedPropCount;
  }, [selectedPropCount, setReportPayload]);

  const handleToggle = useCallback((itemLabel) => {
    if (!canUpdate) return;
    const payloadKey = columnMapping[itemLabel];
    if (payloadKey && setReportPayload) {
      setReportPayload((prev) => ({
        ...prev,
        [payloadKey]: !prev[payloadKey],
      }));
    }
  }, [canUpdate, columnMapping, setReportPayload]);

  return (
    <Box height={"100%"} width={"100%"}>
      {Object.entries(columns).map(([sectionName, items]) => {
        if (pickupDuration === "N/A" && sectionName === "Pickup") return null;
        return (
          <Stack
          direction={"column"}
          component={Box}
          key={sectionName}
          gap={1}
          p={1}
        >
          <Typography sx={{ p: 1, fontSize: "13px", fontWeight: 600 }}>{columnTitles[sectionName]}</Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {items.map((item, index) => {
              const payloadKey = columnMapping[item];
              
              // Determine if disabled based on rules
              let isDisabled = false;
              if (mandatoryFields.includes(item)) isDisabled = false;
              // if (mandatoryFields.includes(item)) isDisabled = true;
              
              if (selectedPropCount > 1 && (item === "Date" || item === "DOW")) isDisabled = true; // Rule 4 override

              return (
                <CustomOptions
                  key={`${sectionName}-${index}`}
                  type={item}
                  selected={reportPayload ? reportPayload[payloadKey] : false}
                  onClick={() => {
                    if (!canUpdate) {
                      if (onPermissionDenied) onPermissionDenied();
                      return;
                    }
                    if (!isDisabled) handleToggle(item);
                  }}
                  disabled={isDisabled}
                />
              );
            })}
          </Box>
        </Stack>
        );
      })}
    </Box>
  );
};

export default React.memo(ViewReport);
